import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Alert, Paper, Typography, ButtonBase } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';
import LanguageIcon from '@mui/icons-material/Language';
import PageHeader from '../components/common/PageHeader';
import SearchBar from '../components/common/SearchBar';
import StudentTable from '../components/students/StudentTable';
import StudentFilters from '../components/students/StudentFilters';
import usePermissions from '../hooks/usePermissions';
import useDebounce from '../hooks/useDebounce';
import { getStudents, searchStudents } from '../services/studentService';

const INITIAL_FILTERS = {
  status: '',
  country: '',
  interestedBank: '',
};

const LEAD_TABS = [
  { value: '', label: 'All Leads', icon: <AllInboxIcon />, color: '#1A73E8' },
  { value: 'new_lead', label: 'New Leads', icon: <FiberNewIcon />, color: '#1A73E8' },
  { value: 'connected', label: 'Connected', icon: <ConnectWithoutContactIcon />, color: '#F9AB00' },
  { value: 'not_interested', label: 'Not Interested', icon: <ThumbDownIcon />, color: '#D93025' },
  { value: 'interested', label: 'Interested', icon: <ThumbUpIcon />, color: '#1E8E3E' },
  { value: 'online_lead', label: 'Online Leads', icon: <LanguageIcon />, color: '#7C3AED' },
];

const BRANCH_TABS = [
  { value: '', label: 'All Branches', icon: <LocationCityIcon /> },
  { value: 'Warangal', label: 'Warangal', icon: <LocationCityIcon /> },
  { value: 'Hyderabad', label: 'Hyderabad', icon: <LocationCityIcon /> },
];


const SubNavTab = ({ icon, label, active, color, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      px: 2.5,
      py: 1.25,
      minWidth: 80,
      position: 'relative',
      borderRadius: 1,
      color: active ? (color || '#1A73E8') : '#5F6368',
      backgroundColor: active ? `${color || '#1A73E8'}0A` : 'transparent',
      transition: 'all 0.2s',
      '&:hover': {
        backgroundColor: active ? `${color || '#1A73E8'}12` : '#F1F3F4',
      },
      '&::after': active
        ? {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 12,
            right: 12,
            height: 2.5,
            borderRadius: '2px 2px 0 0',
            backgroundColor: color || '#1A73E8',
          }
        : {},
    }}
  >
    {React.cloneElement(icon, { sx: { fontSize: 20, mb: 0.25 } })}
    <Typography
      sx={{
        fontSize: '0.6875rem',
        fontWeight: active ? 600 : 500,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  </ButtonBase>
);

const StudentsPage = () => {
  const navigate = useNavigate();
  const { canCreateStudent } = usePermissions();

  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [branchFilter, setBranchFilter] = useState('');
  const [leadFilter, setLeadFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const debouncedSearch = useDebounce(search, 500);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: page + 1,
        limit: pageSize,
        ...(filters.status && { status: filters.status }),
        ...(filters.country && { country: filters.country }),
        ...(filters.interestedBank && { interestedBank: filters.interestedBank }),
        ...(branchFilter && { branch: branchFilter }),
        ...(leadFilter === 'online_lead'
          ? { source: 'Online Lead' }
          : leadFilter ? { leadStatus: leadFilter } : {}),
      };

      let response;

      if (debouncedSearch.trim()) {
        response = await searchStudents({ ...params, q: debouncedSearch.trim() });
      } else {
        response = await getStudents(params);
      }

      const data = response.data?.data;
      setStudents(data?.students || []);
      setTotal(data?.total || 0);
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || 'Failed to load students';
      setError(message);
      setStudents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, debouncedSearch, branchFilter, leadFilter]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, filters, branchFilter, leadFilter]);

  const handlePageChange = (newPage, newPageSize) => {
    setPage(newPage);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setPage(0);
    }
  };

  const handleRowClick = (student) => {
    navigate(`/students/${student._id || student.id}`);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setSearch('');
  };

  return (
    <Box>
      <PageHeader
        title="Students"
        breadcrumbs={[
          { label: 'Dashboard', link: '/dashboard' },
          { label: 'Students' },
        ]}
      >
        {canCreateStudent && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/students/new')}
            sx={{ borderRadius: 2 }}
          >
            Add Student
          </Button>
        )}
      </PageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Lead Status Sub-Nav */}
      <Paper
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          mb: 2,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #DADCE0',
        }}
        elevation={0}
      >
        {LEAD_TABS.map((tab) => (
          <SubNavTab
            key={tab.value}
            icon={tab.icon}
            label={tab.label}
            color={tab.color}
            active={leadFilter === tab.value}
            onClick={() => setLeadFilter(tab.value)}
          />
        ))}
        <Box sx={{ flex: 1 }} />
        {/* Branch Sub-Nav inline */}
        <Box sx={{ display: 'flex', alignItems: 'stretch', borderLeft: '1px solid #E8EAED' }}>
          {BRANCH_TABS.map((tab) => (
            <SubNavTab
              key={tab.value}
              icon={tab.icon}
              label={tab.label}
              color="#1A73E8"
              active={branchFilter === tab.value}
              onClick={() => setBranchFilter(tab.value)}
            />
          ))}
        </Box>
      </Paper>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search by name, phone, file number..."
          />
        </Box>
        <StudentFilters
          filters={filters}
          onChange={handleFilterChange}
          onClear={handleClearFilters}
        />
      </Paper>

      {/* Student Data Grid */}
      <Paper sx={{ p: 0, borderRadius: 2 }}>
        <StudentTable
          students={students}
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onRowClick={handleRowClick}
          loading={loading}
        />
      </Paper>
    </Box>
  );
};

export default StudentsPage;
